import { Box, Grid, Paper } from "@mui/material";
import { IViolationResult } from "@vmosolutions/oc-interfaces";
import chroma from "chroma-js";
import moment from "moment-timezone";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { ISelectedCrewDays } from "src/redux/reducers/CrewScheduleReducer";
import { FilterSearchTable } from "../FilterSearchTable/FilterSearchTable";
import { ITableColumn } from "../FilterSearchTable/TableComponents/TableHeader";

interface ICrewMemberViolationsProps {
  selectedCrewDays: ISelectedCrewDays;
}

export const returnCrewViolationInfo: ITableColumn[] = [
  {
    id: "regulation",
    value: "regulation",
    index: 0,
    numeric: false,
    label: "Reg",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "title",
    value: "title",
    index: 1,
    numeric: false,
    label: "Title",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "cause",
    value: "cause",
    index: 1,
    numeric: false,
    label: "Cause",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "start",
    value: "start",
    index: 2,
    numeric: false,
    label: "Start",
    type: "date",
    width: 5,
    visible: true,
    filter: false,
  },
  {
    id: "end",
    value: "end",
    index: 3,
    numeric: false,
    label: "End",
    type: "date",
    width: 5,
    visible: true,
    filter: false,
  },
];

export const pairingStatusDesc = {
  tableTitle: "Violation Errors",
  tableDescription: "",
  noMessage: "No Errors found",
};

export const CrewMemberViolations = (props: ICrewMemberViolationsProps): JSX.Element => {
  const { selectedCrewDays } = props;

  const crewViolations = useSelector((state: IRootState) => state.crewSchedule.crewViolations);
  const loadedViolations = useSelector((state: IRootState) => state.crewSchedule.loadedViolations);
  const crewMemberViolations = useMemo(
    () => crewViolations?.[selectedCrewDays?.crewId],
    [crewViolations, selectedCrewDays]
  );

  const crewMemberViolationsOnDay: any[] = useMemo(() => {
    const violations: any[] = [];
    const violationSet = new Set<string>();
    if (!crewMemberViolations || !selectedCrewDays || !loadedViolations) {
      return [];
    }
    selectedCrewDays.selectedTimes.forEach((selectedCrewDay: number) => {
      const day = moment.utc(selectedCrewDay);
      const vios = Object.values(crewMemberViolations?.violations)
        .flatMap(({ regulationResults }) => {
          return regulationResults;
        })
        .filter((vio: IViolationResult) => {
          if (!vio.period) {
            return false;
          }
          const periodStart = moment.utc(vio.period.start).startOf("day");
          const periodEnd = moment.utc(vio.period.end).endOf("day");
          const isBetween = day.isSameOrAfter(periodStart) && day.isSameOrBefore(periodEnd);
          return vio.status === "fail" && isBetween && !vio.noShow;
        });
      const violation: any[] = [];
      vios.forEach((vio: IViolationResult) => {
        if (violationSet.has(vio._id)) {
          return;
        }

        violationSet.add(vio._id);
        violation.push({
          id: vio._id,
          title: vio.title,
          regulation: vio.regulation,
          cause: vio.cause,
          start: vio.period.start,
          end: vio.period.end,
        });
      });
      violations.push(...violation);
    });

    return violations;
  }, [crewMemberViolations, selectedCrewDays, loadedViolations]);
  if (!selectedCrewDays || !selectedCrewDays.crewId) {
    return <></>;
  }
  return crewMemberViolationsOnDay.length > 0 ? (
    <Grid container spacing={0}>
      {crewMemberViolationsOnDay.map((violation) => {
        return (
          <Paper
            key={violation.id}
            elevation={4}
            sx={(theme) => ({
              my: "4px !important",
              mx: 0,
              border: `1px solid ${theme.palette.error.dark}`,
              borderRadius: "4px",
              p: 0.75,
              backgroundColor: chroma(theme.palette.error.dark).darken(2).desaturate(1).css(),
              fontSize: "0.7em",
              width: "100%",
            })}
          >
            <Box sx={{ fontWeight: 900, fontSize: "1.2em" }}>{violation.title}</Box>
            <Box>
              {violation.regulation}
              {" - "}
              {violation.cause}
            </Box>
            <Box sx={{ pt: "2px", display: "flex", justifyContent: "space-between" }}>
              <Box>Start {moment(violation.start).format("MMM-DD-YYYY hh:mm")}</Box>
              <Box>End {moment(violation.end).format("MMM-DD-YYYY hh:mm")}</Box>
            </Box>
          </Paper>
        );
      })}
    </Grid>
  ) : (
    <Paper
      sx={(theme) => ({
        my: "4px !important",
        mx: 0,
        border: `1px solid ${chroma(theme.palette.success.dark).darken(1).css()}`,
        borderRadius: "4px",
        p: 0.75,
        backgroundColor: chroma(theme.palette.success.dark).darken(2).desaturate(1).css(),
        fontSize: "0.7em",
        textAlign: "center",
      })}
    >
      There are no violations
    </Paper>
  );
};
